import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  ShieldCheck, Ban, RotateCcw, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, Check,
  Search, Eye, X, ExternalLink, User as UserIcon, AlertTriangle, Crown, Clock, Star, Coins,
  Users as UsersIcon, Activity, CheckCircle2, UserX, MoreVertical, Download, Flag, Calendar
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminService from "../../sevices/admin.service";
import { toastSuccess, toastError } from "../../components/common/toastNotification";
import { timeAgo, formatLabel, capitalizeName, fixPhotoUrl } from "../../lib/utils";
import SafeImage from "../../components/common/SafeImage";

const TABS = ["All Users", "Verified", "Unverified", "Active", "Banned", "Subscribed", "Coins"];

const TAB_TO_FILTER = {
  "All Users": "all",
  "Verified": "verified",
  "Unverified": "unverified",
  "Active": "active",
  "Banned": "banned",
  "Subscribed": "subscribed",
  "Coins": "coins",
};

const TIME_RANGES = [
  { label: "Lifetime", value: "lifetime" },
  { label: "Today (24h)", value: "today" },
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last 30 Days", value: "last30days" },
  { label: "Last Year", value: "lastyear" },
];

/* ─── Custom Sleek Floating Date Filter Dropdown ────────────────── */
const DateFilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = TIME_RANGES.find((tr) => tr.value === value) || TIME_RANGES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-700 hover:text-gray-900 text-xs font-semibold rounded-xl shadow-xs hover:border-purple-300 hover:bg-purple-50/40 transition-all cursor-pointer"
      >
        <Calendar className="w-3.5 h-3.5 text-purple-600" />
        <span>{selectedOption.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180 text-purple-600" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-44 bg-white rounded-2xl shadow-xl border border-gray-100 py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="px-3.5 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Timeframe Range
          </div>
          {TIME_RANGES.map((tr) => {
            const isSelected = tr.value === value;
            return (
              <button
                key={tr.value}
                type="button"
                onClick={() => {
                  onChange(tr.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3.5 py-2 text-xs font-semibold flex items-center justify-between transition-colors ${isSelected
                  ? "bg-purple-50 text-purple-700 font-bold"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <span>{tr.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5 text-purple-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const formatDob = (val) => {
  if (!val) return "—";
  if (Array.isArray(val)) {
    const [y, mo, d] = val;
    return new Date(y, mo - 1, d).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  }
  const d = new Date(val);
  return isNaN(d) ? val : d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

const formatJoinedDate = (val) => {
  if (!val) return "—";
  let d;
  if (Array.isArray(val)) {
    const [y, mo, day] = val;
    d = new Date(y, mo - 1, day);
  } else {
    d = new Date(val);
  }
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

/* ─── Confirmation Modal ─────────────────────────────────────────── */
const ConfirmModal = ({ config, onConfirm, onCancel, loading }) => {
  const [checked, setChecked] = useState(false);
  if (!config) return null;

  const { type, user } = config;
  const normType = (type || "").toLowerCase();

  const isUnban = normType === "unban" || normType.includes("unban") || normType.includes("reactivate");
  const isBan = !isUnban && (normType === "ban" || normType.includes("ban"));
  const isUnverify = normType === "unverify" || normType.includes("unverify");
  const isVerify = !isUnverify && (normType === "verify" || normType.includes("verify") || normType.includes("approve"));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100">
        {/* Top stripe */}
        {isBan && <div className="h-1.5 w-full bg-red-500" />}
        {isUnban && <div className="h-1.5 w-full bg-green-600" />}
        {isVerify && <div className="h-1.5 w-full bg-blue-600" />}
        {isUnverify && <div className="h-1.5 w-full bg-amber-500" />}
        {!isBan && !isUnban && !isVerify && !isUnverify && <div className="h-1.5 w-full bg-purple-600" />}

        <div className="p-6 space-y-4">
          {/* Icon + title */}
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isBan ? "bg-red-50 text-red-600" : isUnban ? "bg-green-50 text-green-600" : isUnverify ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600"
              }`}>
              {isBan && <AlertTriangle className="w-5 h-5" />}
              {isUnban && <RotateCcw className="w-5 h-5" />}
              {isVerify && <ShieldCheck className="w-5 h-5" />}
              {isUnverify && <ShieldCheck className="w-5 h-5" />}
              {!isBan && !isUnban && !isVerify && !isUnverify && <RotateCcw className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base leading-tight">
                {isBan && "Ban this user?"}
                {isUnban && "Unban this user?"}
                {isVerify && "Approve identity?"}
                {isUnverify && "Remove verification?"}
                {!isBan && !isUnban && !isVerify && !isUnverify && "Confirm action?"}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                {capitalizeName(user?.fullName) || user?.email}
              </p>
            </div>
          </div>

          {/* Description */}
          {isBan && (
            <div className="rounded-xl p-3.5 text-xs leading-relaxed bg-red-50 border border-red-100 text-red-800">
              <p className="font-semibold mb-1">This action will immediately:</p>
              <ul className="space-y-1 list-disc list-inside text-red-700">
                <li>Deactivate the account</li>
                <li>Revoke active sessions and FCM tokens</li>
                <li>Hide profile from search and discovery</li>
              </ul>
            </div>
          )}
          {isUnban && (
            <div className="rounded-xl p-3.5 text-xs leading-relaxed bg-green-50 border border-green-100 text-green-800">
              <p>Reactivate account and restore full access to Signova services.</p>
            </div>
          )}
          {isVerify && (
            <div className="rounded-xl p-3.5 text-xs leading-relaxed bg-blue-50 border border-blue-100 text-blue-800">
              <p className="font-semibold mb-1">Identity Check Passed?</p>
              <p>Selfie matches government ID photo and user details look genuine.</p>
            </div>
          )}
          {isUnverify && (
            <div className="rounded-xl p-3.5 text-xs leading-relaxed bg-amber-50 border border-amber-100 text-amber-800">
              <p>Remove identity verification badge from <strong>{user?.fullName || user?.email}</strong>.</p>
            </div>
          )}

          {/* Checkbox for ban */}
          {isBan && (
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-xs text-gray-500">
                I understand this will disable access for this user
              </span>
            </label>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-xs font-bold hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>

            {isBan && (
              <button
                type="button"
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                disabled={loading || !checked}
                onClick={onConfirm}
              >
                {loading ? "Banning…" : "Yes, Ban User"}
              </button>
            )}

            {isUnban && (
              <button
                type="button"
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                disabled={loading}
                onClick={onConfirm}
              >
                {loading ? "Unbanning…" : "Yes, Unban User"}
              </button>
            )}

            {isVerify && (
              <button
                type="button"
                className="flex-1 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                disabled={loading}
                onClick={onConfirm}
              >
                {loading ? "Approving…" : "Approve Identity"}
              </button>
            )}

            {isUnverify && (
              <button
                type="button"
                className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                disabled={loading}
                onClick={onConfirm}
              >
                {loading ? "Removing…" : "Remove Badge"}
              </button>
            )}

            {!isBan && !isUnban && !isVerify && !isUnverify && (
              <button
                type="button"
                className="flex-1 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                disabled={loading}
                onClick={onConfirm}
              >
                {loading ? "Processing…" : "Confirm"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Credit Adjust Modal ────────────────────────────────────────── */
const CreditModal = ({ user, onConfirm, onCancel, loading }) => {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("add");
  if (!user) return null;
  const finalAmount = mode === "add" ? Math.abs(Number(amount)) : -Math.abs(Number(amount));
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100">
        <div className="h-1.5 w-full bg-purple-600" />
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0 text-purple-600">
              <Star className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base leading-tight">Adjust Credits</h3>
              <p className="text-xs text-gray-500 mt-0.5">{capitalizeName(user.fullName) || user.email} · Current: <span className="font-semibold text-purple-700">{user.credits ?? 0}</span></p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMode("add")} className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-all ${mode === "add" ? "bg-purple-600 text-white border-purple-600 shadow-sm" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>+ Add Credits</button>
            <button onClick={() => setMode("remove")} className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-all ${mode === "remove" ? "bg-red-500 text-white border-red-500 shadow-sm" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>− Remove Credits</button>
          </div>
          <input
            type="number"
            min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter credit amount"
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/20"
          />
          <div className="flex gap-2 pt-1">
            <button onClick={onCancel} className="flex-1 px-4 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
            <button
              disabled={loading || !amount || Number(amount) <= 0}
              onClick={() => onConfirm(finalAmount)}
              className="flex-1 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all disabled:opacity-50 shadow-sm"
              style={{ backgroundColor: mode === "add" ? "#8b5cf6" : "#ef4444" }}
            >
              {loading ? "Updating…" : `${mode === "add" ? "Add" : "Remove"} ${amount || 0}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Custom Interactive Export Modal ─────────────────────────────── */
const ExportModal = ({ isOpen, onClose, onExport, currentCategory, currentTimeRange, exporting }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTimeRange, setSelectedTimeRange] = useState("lifetime");

  useEffect(() => {
    if (isOpen) {
      // Map activeTab string to category option key
      const catMap = {
        "All Users": "all",
        "Verified": "verified",
        "Unverified": "unverified",
        "Active": "active",
        "Banned": "banned",
        "Subscribed": "subscribed",
        "Coins": "coins",
      };
      setSelectedCategory(catMap[currentCategory] || "all");
      setSelectedTimeRange(currentTimeRange || "lifetime");
    }
  }, [isOpen, currentCategory, currentTimeRange]);

  if (!isOpen) return null;

  const CATEGORY_OPTIONS = [
    { value: "all", label: "All Users", desc: "Every registered member", icon: UsersIcon },
    { value: "verified", label: "Verified", desc: "Identity checked", icon: ShieldCheck },
    { value: "unverified", label: "Unverified", desc: "Pending or non-verified", icon: ShieldCheck },
    { value: "active", label: "Active", desc: "Currently active members", icon: CheckCircle2 },
    { value: "banned", label: "Banned", desc: "Suspended or blocked", icon: Ban },
    { value: "subscribed", label: "Subscribed", desc: "VIP/Premium members", icon: Star },
    { value: "coins", label: "Coins", desc: "Users with coins (most coins first)", icon: Coins },
  ];

  const TIMELINE_OPTIONS = [
    { value: "lifetime", label: "All Time (Lifetime)", desc: "Complete history" },
    { value: "today", label: "Today (24h)", desc: "Last 24 hours" },
    { value: "last7days", label: "Last 7 Days", desc: "Past week" },
    { value: "last30days", label: "Last 30 Days", desc: "Past month" },
    { value: "lastyear", label: "Last Year", desc: "Past 365 days" },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-5 sm:p-6 bg-gradient-to-r from-purple-800 to-indigo-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center text-purple-200">
              <Download className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-base sm:text-lg leading-tight">Export Users Data</h3>
              <p className="text-xs text-purple-200 mt-0.5 font-medium">Select category and timeline criteria for your CSV report.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-purple-200 transition-colors cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Section 1: User Category Filter */}
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-gray-500 block mb-2.5">
              1. What users do you want to export?
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {CATEGORY_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isSelected = selectedCategory === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSelectedCategory(opt.value)}
                    className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between cursor-pointer ${isSelected
                      ? "border-purple-600 bg-purple-50/70 ring-2 ring-purple-600/20 text-purple-900 shadow-xs"
                      : "border-gray-200 bg-white hover:border-gray-300 text-gray-700 hover:bg-gray-50/50"
                      }`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <Icon className={`w-4 h-4 ${isSelected ? "text-purple-600" : "text-gray-400"}`} />
                      <span className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${isSelected ? "border-purple-600 bg-purple-600 text-white" : "border-gray-300"
                        }`}>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-bold leading-tight">{opt.label}</p>
                      <p className="text-[10px] text-gray-400 font-normal mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section 2: Timeline Date Range */}
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-gray-500 block mb-2.5">
              2. From which timeline / date range?
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TIMELINE_OPTIONS.map((opt) => {
                const isSelected = selectedTimeRange === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSelectedTimeRange(opt.value)}
                    className={`px-3 py-2.5 rounded-xl border text-left transition-all cursor-pointer ${isSelected
                      ? "border-purple-600 bg-purple-50/70 text-purple-900 font-bold ring-1 ring-purple-600/30 shadow-xs"
                      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 text-xs font-medium"
                      }`}
                  >
                    <div className="text-xs font-bold leading-tight">{opt.label}</div>
                    <div className="text-[10px] text-gray-400 font-normal mt-0.5">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 text-xs font-bold hover:bg-gray-100 transition-colors disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onExport(selectedCategory, selectedTimeRange)}
            disabled={exporting}
            className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white text-xs font-bold shadow-md shadow-purple-600/20 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {exporting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Generating CSV...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Download CSV</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Avatar Component ───────────────────────────────────────────── */
const Avatar = ({ user, size = "md" }) => {
  const sz = size === "lg" ? "w-14 h-14 text-lg" : "w-9 h-9 text-xs";
  const src = fixPhotoUrl(user.photoUrl1);
  return (
    <div className="relative flex-shrink-0">
      <SafeImage
        src={src}
        alt={user.fullName || "User"}
        className={`${sz} rounded-full object-cover border border-gray-100 shadow-xs`}
        fallback={
          <div className={`${sz} rounded-full bg-gradient-to-br from-purple-100 to-pink-100 text-purple-700 flex items-center justify-center font-bold shadow-xs border border-purple-200/50`}>
            {user.fullName?.[0]?.toUpperCase() || <UserIcon className="w-4 h-4 text-purple-600" />}
          </div>
        }
      />
      <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${user.active ? "bg-emerald-500" : "bg-red-400"}`} />
    </div>
  );
};

const ImagePreview = ({ src, label }) => {
  const url = fixPhotoUrl(src);
  if (!url) return (
    <div className="flex flex-col items-center justify-center h-36 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-400 text-xs gap-1.5">
      <UserIcon className="w-6 h-6 opacity-40" />
      <span>{label} not uploaded</span>
    </div>
  );
  return (
    <div className="relative group overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
      <SafeImage
        src={url}
        alt={label}
        className="w-full h-36 object-cover"
        fallback={
          <div className="flex flex-col items-center justify-center h-36 text-gray-400 text-xs gap-1.5">
            <UserIcon className="w-6 h-6 opacity-40" />
            <span>Unable to preview</span>
          </div>
        }
      />
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  );
};

const UserDrawer = ({ user, onClose, onConfirmAction, onAdjustCredits, actionLoading, loading }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!user) {
    if (loading) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center justify-center gap-3 border border-gray-100 z-10 animate-in fade-in zoom-in-95 duration-150">
            <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
            <p className="text-xs font-semibold text-gray-700">Loading user profile...</p>
          </div>
        </div>
      );
    }
    return null;
  }

  const field = (label, value) => (
    <div>
      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-0.5">{label}</p>
      <p className="text-xs font-semibold text-gray-800">{value ?? "—"}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col overflow-hidden border border-gray-100" style={{ maxHeight: "calc(100vh - 3rem)" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
          <h2 className="text-sm font-bold text-gray-900">Member Profile Details</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* Main User Card Header */}
          <div className="flex items-center gap-4 p-3.5 rounded-xl bg-purple-50/40 border border-purple-100/60">
            <Avatar user={user} size="lg" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-gray-900 text-base leading-tight truncate">{capitalizeName(user.fullName) || "—"}</p>
              <p className="text-xs text-gray-500 truncate mt-0.5">{user.email}</p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${user.active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {user.active ? "Active Member" : "Account Banned"}
                </span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${user.identityVerified ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                  {user.identityVerified ? "ID Verified" : "Unverified"}
                </span>
                {user.isSubscribed ? (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                    <Crown className="w-2.5 h-2.5 text-purple-600 fill-purple-600" /> Subscribed VIP
                  </span>
                ) : (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    Free Plan
                  </span>
                )}
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
                  🪙 {user.credits ?? 0} Coins
                </span>
                {user.role === "ADMIN" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Admin</span>}
              </div>
            </div>
          </div>

          {/* Profile Details Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50/60 p-3.5 rounded-xl border border-gray-100">
            {field("Gender", formatLabel(user.gender))}
            {field("Date of Birth", formatDob(user.dateOfBirth))}
            {field("Plan", user.isSubscribed ? "⭐ VIP Subscribed" : "Free Plan")}
            {field("Coins Balance", `🪙 ${user.credits ?? 0} Coins`)}
            {field("Nationality", formatLabel(user.nationality))}
            {field("Occupation", formatLabel(user.occupation))}
            {field("Hearing Status", formatLabel(user.hearingStatus))}
            {field("Last Active", timeAgo(user.lastSeen))}
            {field("Joined", timeAgo(user.createdAt))}
          </div>

          {/* Bio */}
          {user.selfDescription && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">About Me</p>
              <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-3 border border-gray-100">{user.selfDescription}</p>
            </div>
          )}

          {/* Photos */}
          {[user.photoUrl1, user.photoUrl2, user.photoUrl3, user.photoUrl4, user.photoUrl5, user.photoUrl6].some(Boolean) && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Uploaded Photos</p>
              <div className="grid grid-cols-3 gap-2">
                {[user.photoUrl1, user.photoUrl2, user.photoUrl3, user.photoUrl4, user.photoUrl5, user.photoUrl6].map((url, i) => {
                  const fixed = fixPhotoUrl(url);
                  return fixed ? (
                    <div key={i} className="relative group overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                      <SafeImage
                        src={fixed}
                        alt={`Photo ${i + 1}`}
                        className="w-full aspect-square object-cover"
                        fallback={
                          <div className="w-full aspect-square flex items-center justify-center text-gray-300">
                            <UserIcon className="w-6 h-6" />
                          </div>
                        }
                      />
                      <a href={fixed} target="_blank" rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                        <ExternalLink className="w-4 h-4 text-white" />
                      </a>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {/* Identity docs */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Verification Documents</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] font-medium text-gray-500 mb-1">Selfie Verification</p>
                <ImagePreview src={user.selfieUrl} label="Selfie" />
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500 mb-1">Government Issued ID</p>
                <ImagePreview src={user.governmentIdUrl} label="Government ID" />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row gap-2">
          {!user.identityVerified && user.selfieUrl && user.governmentIdUrl && (
            <button
              className="flex-1 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-all shadow-xs"
              disabled={actionLoading === `verify-${user.id}`}
              onClick={() => onConfirmAction("verify", user)}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Approve Verification
            </button>
          )}
          {user.active ? (
            <button
              className="flex-1 px-3 py-2 rounded-xl border border-red-200 bg-white hover:bg-red-50 text-red-600 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
              disabled={actionLoading === `ban-${user.id}`}
              onClick={() => onConfirmAction("ban", user)}
            >
              <Ban className="w-3.5 h-3.5" />
              Ban User
            </button>
          ) : (
            <button
              className="flex-1 px-3 py-2 rounded-xl border border-green-200 bg-white hover:bg-green-50 text-green-600 text-xs font-semibold flex items-center justify-center gap-1.5 transition-all"
              disabled={actionLoading === `unban-${user.id}`}
              onClick={() => onConfirmAction("unban", user)}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Unban User
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Users Page ────────────────────────────────────────────── */
const Users = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialFilter = (() => {
    const param = new URLSearchParams(location.search).get("filter") || "";
    if (param.toLowerCase() === "all") return "All Users";
    const match = TABS.find((t) => t.toLowerCase().includes(param.toLowerCase()));
    return match || "All Users";
  })();

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef(null);
  const searchInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(initialFilter);
  const [timeRange, setTimeRange] = useState("lifetime");

  // Top Cards Analytics State
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    verifiedUsers: 0,
    bannedUsers: 0,
  });

  const [actionLoading, setActionLoading] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [creditTarget, setCreditTarget] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const params = useParams();
  const targetResolvedRef = useRef(null);

  // Auto-select user or filter search query when navigated from Verification / Reports / Analytics / Dashboard
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const targetUserId =
      params.userId ||
      searchParams.get("userId") ||
      searchParams.get("id") ||
      location.state?.viewUserId ||
      location.state?.userId ||
      location.state?.user?.id;

    const targetEmail =
      searchParams.get("email") ||
      searchParams.get("search") ||
      location.state?.email ||
      location.state?.search ||
      location.state?.user?.email;

    if (targetEmail && !search) {
      setSearch(targetEmail);
      setDebouncedSearch(targetEmail);
    }

    if (targetUserId) {
      if (targetResolvedRef.current === String(targetUserId) && selectedUser) {
        return;
      }

      // Check if user is already in current table list with full details
      const existingUser = users.find(
        (u) => String(u.id) === String(targetUserId) || (targetEmail && u.email === targetEmail)
      );

      if (existingUser) {
        setSelectedUser(existingUser);
        targetResolvedRef.current = String(targetUserId);
      } else if (location.state?.user) {
        // Optimistically set initial sparse data so drawer opens immediately
        setSelectedUser(location.state.user);
      }

      // Always fetch fresh authoritative full user profile from backend to ensure complete fields
      setProfileLoading(true);
      AdminService.getUserById(targetUserId)
        .then((res) => {
          if (res?.data?.data) {
            setSelectedUser(res.data.data);
            targetResolvedRef.current = String(targetUserId);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch full user profile:", err);
          if (!existingUser && !location.state?.user) {
            toastError("Failed to load user profile");
          }
        })
        .finally(() => {
          setProfileLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.userId, location.search, location.state]);

  const handleCloseDrawer = () => {
    const fromPath = location.state?.from;
    const returnUser = location.state?.user || selectedUser;
    const returnReport = location.state?.returnReport;
    setSelectedUser(null);
    targetResolvedRef.current = null;

    if (fromPath) {
      navigate(fromPath, { state: { returnUser, returnReport } });
    } else if (returnReport) {
      navigate("/reports", { state: { returnReport } });
    } else if (params.userId || new URLSearchParams(location.search).get("userId")) {
      const sp = new URLSearchParams(location.search);
      sp.delete("userId");
      sp.delete("id");
      const qs = sp.toString() ? `?${sp.toString()}` : "";
      navigate(`/users${qs}`, { replace: true, state: {} });
    }
  };

  const handleDropdownToggle = (e, userId) => {
    e.stopPropagation();
    if (openDropdownId === userId) {
      setOpenDropdownId(null);
      setDropdownPos(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const dropdownHeight = 210;
      const spaceBelow = window.innerHeight - rect.bottom;
      const showUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      setDropdownPos({
        top: showUp ? Math.max(10, rect.top - dropdownHeight) : Math.min(window.innerHeight - dropdownHeight - 10, rect.bottom + 4),
        left: Math.max(16, rect.right - 176),
      });
      setOpenDropdownId(userId);
    }
  };

  // Close table row dropdown when clicking outside, scrolling, or resizing
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".row-action-dropdown") && !e.target.closest(".fixed-action-menu")) {
        setOpenDropdownId(null);
        setDropdownPos(null);
      }
    };
    const handleScrollOrResize = () => {
      setOpenDropdownId(null);
      setDropdownPos(null);
    };

    document.addEventListener("click", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, []);

  // Fetch Analytics for Cards
  const fetchAnalytics = useCallback(() => {
    AdminService.getAnalytics()
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setAnalytics({
            totalUsers: data.totalUsers || 0,
            activeUsers: data.activeUsers ?? (data.totalUsers ? data.totalUsers - (data.bannedUsers || 0) : 0),
            activeUsersLast24h: data.activeUsersLast24h || 0,
            verifiedUsers: data.verifiedUsers || 0,
            bannedUsers: data.bannedUsers || 0,
          });
        }
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    const fetchSize = timeRange !== "lifetime" ? 1000 : 10;
    const fetchPage = timeRange !== "lifetime" ? 0 : page;
    AdminService.getUsers(fetchPage, fetchSize, TAB_TO_FILTER[activeTab] || "all", debouncedSearch)
      .then((res) => {
        const data = res?.data?.data;
        setUsers(data?.content || []);
        setTotalPages(data?.totalPages || 0);
        setTotalElements(data?.totalElements || 0);
      })
      .catch(() => toastError("Failed to load users"))
      .finally(() => setLoading(false));
  }, [page, activeTab, debouncedSearch, timeRange]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openConfirm = (type, user) => {
    setOpenDropdownId(null);
    setConfirmConfig({ type, user });
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setPage(0);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 350);
  };

  const handleConfirmed = async () => {
    if (!confirmConfig) return;
    const { type, user } = confirmConfig;
    const key = `${type}-${user.id}`;
    setActionLoading(key);

    // Optimistically update top KPI cards immediately for instant live UI feedback
    setAnalytics((prev) => {
      let bannedDelta = 0;
      let activeDelta = 0;
      let verifiedDelta = 0;
      if (type === "ban") { bannedDelta = 1; activeDelta = -1; }
      if (type === "unban") { bannedDelta = -1; activeDelta = 1; }
      if (type === "verify") { verifiedDelta = 1; }
      if (type === "unverify") { verifiedDelta = -1; }

      return {
        ...prev,
        bannedUsers: Math.max(0, (prev.bannedUsers || 0) + bannedDelta),
        activeUsers: Math.max(0, (prev.activeUsers || 0) + activeDelta),
        verifiedUsers: Math.max(0, (prev.verifiedUsers || 0) + verifiedDelta),
      };
    });

    try {
      if (type === "ban") await AdminService.banUser(user.id);
      if (type === "unban") await AdminService.unbanUser(user.id);
      if (type === "verify") await AdminService.verifyUser(user.id);
      if (type === "unverify") await AdminService.unverifyUser(user.id);

      const messages = { ban: "User banned", unban: "User unbanned", verify: "Identity verified", unverify: "Verification removed" };
      toastSuccess(messages[type]);
      setConfirmConfig(null);

      // Re-fetch users and fresh backend analytics
      fetchUsers();
      fetchAnalytics();

      if (selectedUser?.id === user.id) {
        setSelectedUser((u) => ({
          ...u,
          active: type === "ban" ? false : type === "unban" ? true : u.active,
          identityVerified: type === "verify" ? true : type === "unverify" ? false : u.identityVerified,
        }));
      }
    } catch {
      toastError("Action failed");
      // Revert/refresh on error
      fetchAnalytics();
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreditAdjust = async (amount) => {
    if (!creditTarget) return;
    setActionLoading(`credit-${creditTarget.id}`);
    try {
      await AdminService.adjustCredits(creditTarget.id, amount);
      toastSuccess(`Credits ${amount > 0 ? "added" : "removed"} successfully`);
      setCreditTarget(null);
      fetchUsers();
      if (selectedUser?.id === creditTarget.id) {
        setSelectedUser((u) => ({ ...u, credits: (u.credits ?? 0) + amount }));
      }
    } catch {
      toastError("Failed to adjust credits");
    } finally {
      setActionLoading(null);
    }
  };

  // Filter users by joined date / createdAt
  const filterUsersByDate = (userList, range) => {
    if (range === "lifetime" || !userList) return userList;
    const now = new Date();
    return userList.filter((u) => {
      if (!u.createdAt) return false;
      let joined;
      if (Array.isArray(u.createdAt)) {
        const [y, mo, day] = u.createdAt;
        joined = new Date(y, mo - 1, day);
      } else {
        joined = new Date(u.createdAt);
      }
      if (isNaN(joined.getTime())) return false;
      const diffHours = (now - joined) / (1000 * 60 * 60);
      const diffDays = diffHours / 24;
      if (range === "today") return diffHours <= 24;
      if (range === "last7days") return diffDays <= 7;
      if (range === "last30days") return diffDays <= 30;
      if (range === "lastyear") return diffDays <= 365;
      return true;
    });
  };

  const isFiltered = timeRange !== "lifetime";
  let displayedUsers = filterUsersByDate(users, timeRange);
  if (activeTab === "Coins" && isFiltered) {
    displayedUsers = [...displayedUsers].sort((a, b) => (Number(b.credits) || 0) - (Number(a.credits) || 0));
  }

  // Dynamic pagination calculations based on backend total or filtered data
  const pageSize = 10;
  const effectiveTotal = isFiltered ? displayedUsers.length : totalElements;
  const effectivePages = isFiltered
    ? Math.max(1, Math.ceil(displayedUsers.length / pageSize))
    : Math.max(1, totalPages || Math.ceil(totalElements / pageSize));

  const safePage = Math.max(0, Math.min(page, effectivePages - 1));

  // Sliding window pagination: exactly 5 distinct page buttons around safePage
  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    if (effectivePages <= maxVisible) {
      return Array.from({ length: effectivePages }, (_, i) => i);
    }
    let start = safePage - Math.floor(maxVisible / 2);
    if (start < 0) {
      start = 0;
    } else if (start + maxVisible > effectivePages) {
      start = effectivePages - maxVisible;
    }
    return Array.from({ length: maxVisible }, (_, i) => start + i);
  };

  // Paginated user slice for table display
  const paginatedUsers = isFiltered
    ? displayedUsers.slice(safePage * pageSize, (safePage + 1) * pageSize)
    : displayedUsers;

  // Execute User Export based on Modal Criteria (category + timeline)
  const handleExecuteExport = async (category, range) => {
    setExporting(true);
    try {
      // 1. Fetch users for the requested category
      const res = await AdminService.getUsers(0, 5000, category, debouncedSearch);
      const rawUsers = res?.data?.data?.content || [];

      // 2. Filter users by requested timeline
      const filteredUsers = filterUsersByDate(rawUsers, range);

      if (!filteredUsers || filteredUsers.length === 0) {
        toastError("No user data matching selected export criteria");
        setExporting(false);
        return;
      }

      // 3. Build CSV content
      const headers = ["User ID", "Full Name", "Email", "Status", "Plan", "Coins", "Verification", "Gender", "Last Seen", "Joined Date"];
      const rows = filteredUsers.map((u) => [
        u.userId || u.id,
        `"${(u.fullName || "").replace(/"/g, '""')}"`,
        `"${u.email || ""}"`,
        u.active ? "Active" : "Banned",
        u.isSubscribed ? "VIP Subscribed" : "Free User",
        u.credits !== undefined && u.credits !== null ? u.credits : 0,
        u.identityVerified ? "Verified" : "Unverified",
        u.gender || "",
        u.lastSeen ? new Date(u.lastSeen).toISOString() : "",
        u.createdAt ? (Array.isArray(u.createdAt) ? new Date(u.createdAt[0], u.createdAt[1] - 1, u.createdAt[2]).toISOString() : new Date(u.createdAt).toISOString()) : "",
      ]);

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `signova_users_${category}_${range}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      const catName = category === "all" ? "All Users" : category;
      const rangeName = TIME_RANGES.find((t) => t.value === range)?.label || range;
      toastSuccess(`Exported ${filteredUsers.length} ${catName} (${rangeName}) successfully!`);
      setShowExportModal(false);
    } catch (err) {
      toastError("Failed to export users data");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="min-h-full bg-gray-50/50 p-4 sm:p-6 lg:p-8 space-y-6">

        {/* Page Title & Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Users</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">
              Manage and review Signova members.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchUsers}
              disabled={loading}
              title="Refresh"
              className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors shadow-xs disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-purple-600" : ""}`} />
            </button>
          </div>
        </div>

        {/* Top 4 Summary Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Total Users */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-xs p-5 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">Total Users</p>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-1">
                {(analytics.totalUsers || totalElements).toLocaleString()}
              </h3>
              <p className="text-xs font-bold text-emerald-600 mt-1.5 flex items-center gap-1">
                <span>↑ 8.4%</span>
                <span className="text-gray-400 font-normal">vs last 7 days</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-pink-50 text-pink-600 flex items-center justify-center flex-shrink-0">
              <UsersIcon className="w-6 h-6" />
            </div>
          </div>

          {/* Card 2: Active Users */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-xs p-5 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">Active Users</p>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-1">
                {(analytics.activeUsers || 0).toLocaleString()}
              </h3>
              <p className="text-xs font-bold text-emerald-600 mt-1.5 flex items-center gap-1">
                <span>{analytics.activeUsersLast24h || 0} online</span>
                <span className="text-gray-400 font-normal">in last 24h</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
              <Activity className="w-6 h-6" />
            </div>
          </div>

          {/* Card 3: Verified Users */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-xs p-5 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">Verified Users</p>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-1">
                {(analytics.verifiedUsers || 0).toLocaleString()}
              </h3>
              <p className="text-xs font-bold text-emerald-600 mt-1.5 flex items-center gap-1">
                <span>↑ 7.3%</span>
                <span className="text-gray-400 font-normal">vs last 7 days</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
          </div>

          {/* Card 4: Suspended / Banned */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-xs p-5 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500">Suspended / Banned</p>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-1">
                {(analytics.bannedUsers || 0).toLocaleString()}
              </h3>
              <p className="text-xs font-bold text-rose-600 mt-1.5 flex items-center gap-1">
                <span>↑ 15.2%</span>
                <span className="text-gray-400 font-normal">vs last 7 days</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center flex-shrink-0">
              <UserX className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Filter Bar & Search Container Card (Matching Reference UI) */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-xs p-4 sm:p-5 space-y-4">
          {/* Top Row: Search Input (left) + Date Filter Dropdown & Export button (right) */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search users by name, email or username..."
                value={search}
                onChange={handleSearchChange}
                className="w-full pl-11 pr-4 h-11 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all text-gray-800 placeholder-gray-400 font-medium"
              />
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0">
              <DateFilterDropdown
                value={timeRange}
                onChange={(newVal) => {
                  setTimeRange(newVal);
                  setPage(0);
                }}
              />

              <button
                type="button"
                onClick={() => setShowExportModal(true)}
                className="inline-flex items-center gap-2 px-5 h-11 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Bottom Row: Tab Filter Pills */}
          <div className="pt-1 border-t border-gray-50">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
              {TABS.map((t) => {
                const isActive = activeTab === t;
                const isCoins = t === "Coins";
                return (
                  <button
                    key={t}
                    onClick={() => { setActiveTab(t); setPage(0); }}
                    className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 cursor-pointer inline-flex items-center gap-1.5 ${isActive
                      ? isCoins
                        ? "bg-amber-100/80 text-amber-800 border border-amber-300 shadow-none"
                        : "bg-pink-100/70 text-pink-600 border border-pink-200/60 shadow-none"
                      : isCoins
                        ? "bg-white border border-gray-200 text-gray-700 hover:bg-amber-50/50 hover:text-amber-800 hover:border-amber-200"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    {isCoins && (
                      <Coins className={`w-3.5 h-3.5 ${isActive ? "text-amber-600" : "text-amber-500"}`} />
                    )}
                    <span>{t}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Modern Users Table Card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-xs">
          <div className="w-full overflow-x-auto min-h-[300px] pb-16">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-gray-600 font-bold text-xs">
                  <th className="px-4 sm:px-6 py-4">User</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Plan</th>
                  <th className="px-4 py-4 text-center">Coins</th>
                  <th className="px-4 py-4">Verification</th>
                  <th className="px-4 py-4 hidden md:table-cell">Joined</th>
                  <th className="px-4 py-4 hidden lg:table-cell">Last Active</th>
                  <th className="px-4 py-4 hidden sm:table-cell text-center">Reports</th>
                  <th className="px-4 sm:px-6 py-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-700">
                {loading ? (
                  Array.from({ length: 7 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                          <div className="space-y-1.5">
                            <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
                            <div className="h-2.5 w-36 bg-gray-200 rounded animate-pulse" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4"><div className="h-5 w-16 bg-gray-200 rounded-full animate-pulse" /></td>
                      <td className="px-4 py-4"><div className="h-5 w-20 bg-gray-200 rounded-full animate-pulse" /></td>
                      <td className="px-4 py-4 text-center"><div className="h-5 w-14 bg-gray-200 rounded-full animate-pulse mx-auto" /></td>
                      <td className="px-4 py-4"><div className="h-5 w-20 bg-gray-200 rounded-full animate-pulse" /></td>
                      <td className="px-4 py-4 hidden md:table-cell"><div className="h-3 w-16 bg-gray-200 rounded-pulse animate-pulse" /></td>
                      <td className="px-4 py-4 hidden lg:table-cell"><div className="h-3 w-20 bg-gray-200 rounded-pulse animate-pulse" /></td>
                      <td className="px-4 py-4 hidden sm:table-cell text-center"><div className="h-5 w-8 bg-gray-200 rounded-full animate-pulse mx-auto" /></td>
                      <td className="px-4 sm:px-6 py-4 text-right"><div className="h-6 w-6 bg-gray-200 rounded-lg animate-pulse ml-auto" /></td>
                    </tr>
                  ))
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-16">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <UserIcon className="w-9 h-9 text-gray-300" />
                        <p className="text-sm font-semibold text-gray-600">No users found</p>
                        <p className="text-xs text-gray-400">Try adjusting your search query, date filter, or tab filters</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/70 transition-colors">
                      {/* User Column */}
                      <td
                        className="px-4 sm:px-6 py-4 cursor-pointer group"
                        onClick={() => setSelectedUser(user)}
                        title="Click to view full user profile"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar user={user} />
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 text-xs sm:text-sm leading-tight truncate group-hover:text-purple-600 transition-colors flex items-center gap-1.5">
                              <span>{capitalizeName(user.fullName) || "—"}</span>
                              <Eye className="w-3.5 h-3.5 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </p>
                            <p className="text-xs text-gray-400 truncate mt-0.5 font-normal">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Status Column */}
                      <td className="px-4 py-4">
                        {user.active ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100/60">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-100/60">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                            Banned
                          </span>
                        )}
                      </td>

                      {/* Plan / Subscription Column */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {user.isSubscribed ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border border-purple-200/80 shadow-2xs">
                            <Crown className="w-3.5 h-3.5 text-purple-600 fill-purple-600" />
                            VIP Subscribed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200/70">
                            Free User
                          </span>
                        )}
                      </td>

                      {/* Coins / Balance Column */}
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50/90 border border-amber-200/80 text-amber-900 font-bold text-xs shadow-2xs">
                          <Coins className="w-3.5 h-3.5 text-amber-600" />
                          <span>{user.credits !== undefined && user.credits !== null ? Number(user.credits).toLocaleString() : 0}</span>
                        </span>
                      </td>

                      {/* Verification Column */}
                      <td className="px-4 py-4">
                        {user.identityVerified ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100/60">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                            Verified
                          </span>
                        ) : !user.identityVerified && (user.selfieUrl || user.governmentIdUrl) ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100/60">
                            <Clock className="w-3.5 h-3.5 text-amber-600" />
                            Pending Review
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200/60">
                            <ShieldCheck className="w-3.5 h-3.5 text-gray-400" />
                            Unverified
                          </span>
                        )}
                      </td>

                      {/* Joined Date */}
                      <td className="px-4 py-4 text-xs text-gray-600 font-medium hidden md:table-cell">
                        {formatJoinedDate(user.createdAt)}
                      </td>

                      {/* Last Active */}
                      <td className="px-4 py-4 text-xs text-gray-600 font-medium hidden lg:table-cell">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {timeAgo(user.lastSeen)}
                        </span>
                      </td>

                      {/* Reports Count */}
                      <td className="px-4 py-4 text-center hidden sm:table-cell text-xs text-gray-600 font-medium">
                        {user.reportCount || 0}
                      </td>

                      {/* Action Popup Dropdown */}
                      <td className="px-4 sm:px-6 py-4 text-right relative row-action-dropdown">
                        <button
                          onClick={(e) => handleDropdownToggle(e, user.id)}
                          className="w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-400 hover:text-gray-700 hover:bg-gray-50 flex items-center justify-center transition-all cursor-pointer ml-auto"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {/* Floating Action Menu (Viewport Fixed to prevent any clipping) */}
                        {openDropdownId === user.id && dropdownPos && (
                          <div
                            style={{
                              position: 'fixed',
                              top: `${dropdownPos.top}px`,
                              left: `${dropdownPos.left}px`,
                              zIndex: 9999
                            }}
                            className="fixed-action-menu w-44 bg-white border border-gray-100 rounded-xl shadow-2xl py-1 text-left text-xs font-medium animate-in fade-in zoom-in-95 duration-100"
                          >
                            <button
                              onClick={() => { setSelectedUser(user); setOpenDropdownId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>View Profile</span>
                            </button>

                            <button
                              onClick={() => { navigate(`/reports?search=${user.email}`); setOpenDropdownId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                            >
                              <Flag className="w-3.5 h-3.5" />
                              <span>View Reports</span>
                            </button>

                            <button
                              onClick={() => { setCreditTarget(user); setOpenDropdownId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                            >
                              <Star className="w-3.5 h-3.5 text-amber-500" />
                              <span>Adjust Credits</span>
                            </button>

                            {user.identityVerified ? (
                              <button
                                onClick={() => openConfirm("unverify", user)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-amber-600 hover:bg-amber-50 transition-colors border-t border-gray-50"
                              >
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span>Remove Verification</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => openConfirm("verify", user)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 transition-colors border-t border-gray-50"
                              >
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span>Verify Identity</span>
                              </button>
                            )}

                            {user.active ? (
                              <button
                                onClick={() => openConfirm("ban", user)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-rose-600 hover:bg-rose-50 transition-colors"
                              >
                                <Ban className="w-3.5 h-3.5" />
                                <span>Ban User</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => openConfirm("unban", user)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-emerald-600 hover:bg-emerald-50 transition-colors"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span>Unban User</span>
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Dynamic Pagination Footer */}
          <div className="px-4 sm:px-6 py-3.5 bg-gray-50/50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
            <p className="font-medium">
              Showing <span className="font-bold text-gray-800">{effectiveTotal === 0 ? 0 : safePage * pageSize + 1}</span> to{" "}
              <span className="font-bold text-gray-800">{Math.min((safePage + 1) * pageSize, effectiveTotal)}</span> of{" "}
              <span className="font-bold text-gray-800">{effectiveTotal.toLocaleString()}</span> members
            </p>

            {/* Dynamic Controls: << < 1 2 3 > >> */}
            <div className="flex items-center gap-1">
              {/* << First Page */}
              <button
                disabled={safePage === 0 || loading}
                onClick={() => setPage(0)}
                title="First Page"
                className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-white transition-colors"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              {/* < Previous Page */}
              <button
                disabled={safePage === 0 || loading}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                title="Previous Page"
                className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Dynamic Page Number Buttons */}
              {getVisiblePageNumbers().map((pageNum) => {
                const isActive = pageNum === safePage;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all cursor-pointer ${isActive
                      ? "bg-purple-600 text-white shadow-xs"
                      : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                      }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}

              {/* > Next Page */}
              <button
                disabled={safePage >= effectivePages - 1 || loading}
                onClick={() => setPage((p) => Math.min(effectivePages - 1, p + 1))}
                title="Next Page"
                className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-white transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* >> Last Page */}
              <button
                disabled={safePage >= effectivePages - 1 || loading}
                onClick={() => setPage(effectivePages - 1)}
                title="Last Page"
                className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-white transition-colors"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Modals and Drawers */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExecuteExport}
        currentCategory={activeTab}
        currentTimeRange={timeRange}
        exporting={exporting}
      />

      <ConfirmModal
        config={confirmConfig}
        onConfirm={handleConfirmed}
        onCancel={() => setConfirmConfig(null)}
        loading={!!actionLoading}
      />

      <CreditModal
        user={creditTarget}
        onConfirm={handleCreditAdjust}
        onCancel={() => setCreditTarget(null)}
        loading={!!actionLoading}
      />

      <UserDrawer
        user={selectedUser}
        onClose={handleCloseDrawer}
        onConfirmAction={openConfirm}
        onAdjustCredits={(u) => setCreditTarget(u)}
        actionLoading={actionLoading}
        loading={profileLoading}
      />
    </>
  );
};

export default Users;
